import React from 'react'
import { Icon } from '../../atoms/Icon'
import type {
  PropertyAttributesDisplayProps,
  PropertyAttributesDisplayTranslations,
  AttributeCategory,
  AttributeField,
} from './PropertyAttributesDisplay.types'
import * as S from './PropertyAttributesDisplay.styles'

// Default English translations for fallback
const DEFAULT_TRANSLATIONS: PropertyAttributesDisplayTranslations = {
  categories: {
    overview: 'Property Overview',
    landBuilding: 'Land & Building',
    energy: 'Energy & Systems',
    exterior: 'Exterior Features',
    interior: 'Interior Features',
    environment: 'Environment & Location',
  },
  fields: {
    propertyType: 'Property Type',
    propertySubtype: 'Property Subtype',
    livingArea: 'Living Area',
    constructionYear: 'Construction Year',
    roomsNb: 'Number of Rooms',
    numberOfBedrooms: 'Bedrooms',
    numberOfBathrooms: 'Bathrooms',
    floor: 'Floor',
    landArea: 'Land Area',
    usableArea: 'Usable Area',
    weightedArea: 'Weighted Area',
    volume: 'Volume',
    buildingHeight: 'Building Height',
    numberOfFloors: 'Number of Floors',
    floorArea: 'Floor Area',
    heatingType: 'Heating Type',
    minergie: 'Minergie Certified',
    solarPotential: 'Solar Potential',
    energyClass: 'Energy Class',
    solarPanelsInstalled: 'Solar Panels Installed',
    solarArea: 'Solar Area',
    heatConsumptionIndex: 'Heat Consumption Index',
    parking: 'Parking Spaces',
    garage: 'Garage Spaces',
    hasBalcony: 'Balcony',
    balconyArea: 'Balcony Area',
    hasTerrace: 'Terrace',
    terraceArea: 'Terrace Area',
    elevator: 'Elevator',
    cellar: 'Cellar',
    attic: 'Attic',
    hasGarden: 'Garden',
    gardenArea: 'Garden Area',
    pool: 'Swimming Pool',
    sauna: 'Sauna',
    fireplace: 'Fireplace',
    airConditioning: 'Air Conditioning',
    condition: 'Overall Condition',
    renovationYear: 'Renovation Year',
    lakeView: 'Lake View',
    mountainView: 'Mountain View',
    noiseLevel: 'Noise Level',
    publicTransportQuality: 'Public Transport Quality',
    exposureOrientation: 'Exposure Orientation',
  },
  units: {
    squareMeters: 'm²',
    cubicMeters: 'm³',
    meters: 'm',
    years: 'years',
    rooms: 'rooms',
    spaces: 'spaces',
  },
  boolean: {
    yes: 'Yes',
    no: 'No',
  },
  emptyState: 'No property attributes available.',
}

const normalizeOptionKey = (value: string): string =>
  value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase()

export const PropertyAttributesDisplay: React.FC<
  PropertyAttributesDisplayProps
> = ({ attributes, className, translations }) => {
  // Merge provided translations with defaults
  const t: PropertyAttributesDisplayTranslations = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
    units: {
      ...DEFAULT_TRANSLATIONS.units,
      ...translations?.units,
    },
    boolean: {
      ...DEFAULT_TRANSLATIONS.boolean,
      ...translations?.boolean,
    },
    options: translations?.options,
    formatUnit: translations?.formatUnit,
  }

  // Helper to format units with pluralization when formatUnit is provided
  const formatPluralUnit = (
    unit: 'rooms' | 'spaces' | 'bedrooms',
    count: number
  ): string => {
    // Use formatUnit if provided (with proper pluralization)
    if (t.formatUnit) {
      return t.formatUnit(unit, count)
    }
    // Fallback to static unit string (no pluralization)
    return `${count} ${t.units[unit]}`
  }

  // Translate enum values based on field key and category
  const translateEnumValue = (
    fieldKey: string,
    value: string,
    propertyType?: string
  ): string => {
    if (!t.options || !value) return value

    // Map field keys to option categories
    const getOptionCategory = (): string | undefined => {
      switch (fieldKey) {
        case 'propertyType':
          return 'propertyType'
        case 'propertySubtype':
          // Use the appropriate subtype category based on propertyType
          return propertyType === 'house' ? 'houseSubtype' : 'apartmentSubtype'
        case 'condition':
          return 'condition'
        case 'heatingType':
          return 'heatingType'
        case 'energyClass':
          return 'energyClass'
        case 'exposureOrientation':
          return 'orientation'
        case 'noiseLevel':
          return 'noise'
        case 'lakeView':
        case 'mountainView':
          return 'view'
        case 'publicTransportQuality':
        case 'solarPotential':
          return 'quality'
        default:
          return undefined
      }
    }

    const category = getOptionCategory()
    if (!category) return value

    const optionKeys = Array.from(new Set([value, normalizeOptionKey(value)]))
    for (const optionKey of optionKeys) {
      const translated = t.options[category]?.[optionKey]
      if (translated) return translated
    }

    // Fallback: for propertySubtype, try the other subtype category
    if (fieldKey === 'propertySubtype') {
      const fallbackCategory =
        category === 'houseSubtype' ? 'apartmentSubtype' : 'houseSubtype'
      for (const optionKey of optionKeys) {
        const fallback = t.options[fallbackCategory]?.[optionKey]
        if (fallback) return fallback
      }
    }

    return value
  }

  const formatValue = (
    value: any,
    formatter?: (value: any) => string
  ): string => {
    if (value === null || value === undefined) return '-'
    if (formatter) return formatter(value)
    if (typeof value === 'boolean') return value ? t.boolean.yes : t.boolean.no
    if (typeof value === 'number') return value.toString()
    return String(value)
  }

  const getFieldLabel = (key: string, fallbackLabel: string): string => {
    return t.fields[key] || fallbackLabel
  }

  const getCategoryTitle = (
    categoryId: string,
    fallbackTitle: string
  ): string => {
    return t.categories[categoryId] || fallbackTitle
  }

  const buildCategories = (): AttributeCategory[] => {
    const categories: AttributeCategory[] = []

    // Property Overview
    if (attributes.overview) {
      const propertyType = attributes.overview.propertyType
      const fields: AttributeField[] = [
        {
          key: 'propertyType',
          label: getFieldLabel('propertyType', 'Property Type'),
          value: attributes.overview.propertyType,
          formatter: (v: string) => translateEnumValue('propertyType', v),
        },
        {
          key: 'propertySubtype',
          label: getFieldLabel('propertySubtype', 'Property Subtype'),
          value: attributes.overview.propertySubtype,
          formatter: (v: string) =>
            translateEnumValue('propertySubtype', v, propertyType),
        },
        {
          key: 'livingArea',
          label: getFieldLabel('livingArea', 'Living Area'),
          value: attributes.overview.livingArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'usableArea',
          label: getFieldLabel('usableArea', 'Usable Area'),
          value: attributes.overview.usableArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'weightedArea',
          label: getFieldLabel('weightedArea', 'Weighted Area'),
          value: attributes.overview.weightedArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'roomsNb',
          label: getFieldLabel('roomsNb', 'Number of Rooms'),
          value: attributes.overview.roomsNb,
          formatter: (v: number) => formatPluralUnit('rooms', v),
        },
        {
          key: 'numberOfBedrooms',
          label: getFieldLabel('numberOfBedrooms', 'Bedrooms'),
          value: attributes.overview.numberOfBedrooms,
          formatter: (v: number) =>
            t.formatUnit ? t.formatUnit('bedrooms', v) : String(v),
        },
        {
          key: 'numberOfBathrooms',
          label: getFieldLabel('numberOfBathrooms', 'Bathrooms'),
          value: attributes.overview.numberOfBathrooms,
          formatter: (v: number) =>
            t.formatUnit ? t.formatUnit('bathrooms', v) : String(v),
        },
        {
          key: 'floor',
          label: getFieldLabel('floor', 'Floor'),
          value: attributes.overview.floor,
        },
        {
          key: 'condition',
          label: getFieldLabel('condition', 'Overall Condition'),
          value: attributes.overview.condition,
          formatter: (v: string) => translateEnumValue('condition', v),
        },
      ].filter((f) => f.value !== null && f.value !== undefined)

      if (fields.length > 0) {
        categories.push({
          id: 'overview',
          title: getCategoryTitle('overview', 'Property Overview'),
          iconName: 'home',
          fields,
        })
      }
    }

    // Land & Building
    if (attributes.landBuilding) {
      const fields: AttributeField[] = [
        {
          key: 'landArea',
          label: getFieldLabel('landArea', 'Land Area'),
          value: attributes.landBuilding.landArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'constructionYear',
          label: getFieldLabel('constructionYear', 'Construction Year'),
          value: attributes.landBuilding.constructionYear,
        },
        {
          key: 'renovationYear',
          label: getFieldLabel('renovationYear', 'Renovation Year'),
          value: attributes.landBuilding.renovationYear,
        },
        {
          key: 'volume',
          label: getFieldLabel('volume', 'Volume'),
          value: attributes.landBuilding.volume,
          formatter: (v: number) => `${v} ${t.units.cubicMeters}`,
        },
        {
          key: 'buildingHeight',
          label: getFieldLabel('buildingHeight', 'Building Height'),
          value: attributes.landBuilding.buildingHeight,
          formatter: (v: number) => `${v} ${t.units.meters}`,
        },
        {
          key: 'numberOfFloors',
          label: getFieldLabel('numberOfFloors', 'Number of Floors'),
          value: attributes.landBuilding.numberOfFloors,
        },
        {
          key: 'floorArea',
          label: getFieldLabel('floorArea', 'Floor Area'),
          value: attributes.landBuilding.floorArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
      ].filter((f) => f.value !== null && f.value !== undefined)

      if (fields.length > 0) {
        categories.push({
          id: 'landBuilding',
          title: getCategoryTitle('landBuilding', 'Land & Building'),
          iconName: 'building',
          fields,
        })
      }
    }

    // Energy & Systems
    if (attributes.energy) {
      const fields: AttributeField[] = [
        {
          key: 'heatingType',
          label: getFieldLabel('heatingType', 'Heating Type'),
          value: attributes.energy.heatingType,
          formatter: (v: string) => translateEnumValue('heatingType', v),
        },
        {
          key: 'minergie',
          label: getFieldLabel('minergie', 'Minergie Certified'),
          value: attributes.energy.minergie,
        },
        {
          key: 'solarPotential',
          label: getFieldLabel('solarPotential', 'Solar Potential'),
          value: attributes.energy.solarPotential,
          formatter: (v: string) => translateEnumValue('solarPotential', v),
        },
        {
          key: 'energyClass',
          label: getFieldLabel('energyClass', 'Energy Class'),
          value: attributes.energy.energyClass,
          formatter: (v: string) => translateEnumValue('energyClass', v),
        },
        {
          key: 'solarPanelsInstalled',
          label: getFieldLabel(
            'solarPanelsInstalled',
            'Solar Panels Installed'
          ),
          value: attributes.energy.solarPanelsInstalled,
        },
        {
          key: 'solarArea',
          label: getFieldLabel('solarArea', 'Solar Area'),
          value: attributes.energy.solarArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'heatConsumptionIndex',
          label: getFieldLabel(
            'heatConsumptionIndex',
            'Heat Consumption Index'
          ),
          value: attributes.energy.heatConsumptionIndex,
        },
      ].filter((f) => f.value !== null && f.value !== undefined)

      if (fields.length > 0) {
        categories.push({
          id: 'energy',
          title: getCategoryTitle('energy', 'Energy & Systems'),
          iconName: 'zap',
          fields,
        })
      }
    }

    // Exterior Features
    if (attributes.exterior) {
      const fields: AttributeField[] = [
        {
          key: 'parking',
          label: getFieldLabel('parking', 'Parking Spaces'),
          value: attributes.exterior.parking,
          formatter: (v: number) => formatPluralUnit('spaces', v),
        },
        {
          key: 'hasBalcony',
          label: getFieldLabel('hasBalcony', 'Balcony'),
          value: attributes.exterior.hasBalcony,
        },
        {
          key: 'balconyArea',
          label: getFieldLabel('balconyArea', 'Balcony Area'),
          value: attributes.exterior.balconyArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'hasTerrace',
          label: getFieldLabel('hasTerrace', 'Terrace'),
          value: attributes.exterior.hasTerrace,
        },
        {
          key: 'terraceArea',
          label: getFieldLabel('terraceArea', 'Terrace Area'),
          value: attributes.exterior.terraceArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'hasGarden',
          label: getFieldLabel('hasGarden', 'Garden'),
          value: attributes.exterior.hasGarden,
        },
        {
          key: 'gardenArea',
          label: getFieldLabel('gardenArea', 'Garden Area'),
          value: attributes.exterior.gardenArea,
          formatter: (v: number) => `${v} ${t.units.squareMeters}`,
        },
        {
          key: 'pool',
          label: getFieldLabel('pool', 'Swimming Pool'),
          value: attributes.exterior.pool,
        },
      ].filter((f) => f.value !== null && f.value !== undefined)

      if (fields.length > 0) {
        categories.push({
          id: 'exterior',
          title: getCategoryTitle('exterior', 'Exterior Features'),
          iconName: 'sun',
          fields,
        })
      }
    }

    // Interior Features
    if (attributes.interior) {
      const fields: AttributeField[] = [
        {
          key: 'garage',
          label: getFieldLabel('garage', 'Garage Spaces'),
          value: attributes.interior.garage,
          formatter: (v: number) => formatPluralUnit('spaces', v),
        },
        {
          key: 'elevator',
          label: getFieldLabel('elevator', 'Elevator'),
          value: attributes.interior.elevator,
        },
        {
          key: 'cellar',
          label: getFieldLabel('cellar', 'Cellar'),
          value: attributes.interior.cellar,
        },
        {
          key: 'attic',
          label: getFieldLabel('attic', 'Attic'),
          value: attributes.interior.attic,
        },
        {
          key: 'sauna',
          label: getFieldLabel('sauna', 'Sauna'),
          value: attributes.interior.sauna,
        },
        {
          key: 'fireplace',
          label: getFieldLabel('fireplace', 'Fireplace'),
          value: attributes.interior.fireplace,
        },
        {
          key: 'airConditioning',
          label: getFieldLabel('airConditioning', 'Air Conditioning'),
          value: attributes.interior.airConditioning,
        },
      ].filter((f) => f.value !== null && f.value !== undefined)

      if (fields.length > 0) {
        categories.push({
          id: 'interior',
          title: getCategoryTitle('interior', 'Interior Features'),
          iconName: 'sofa',
          fields,
        })
      }
    }

    // Environment & Location
    if (attributes.environment) {
      const fields: AttributeField[] = [
        {
          key: 'lakeView',
          label: getFieldLabel('lakeView', 'Lake View'),
          value: attributes.environment.lakeView,
          formatter: (v: string) => translateEnumValue('lakeView', v),
        },
        {
          key: 'mountainView',
          label: getFieldLabel('mountainView', 'Mountain View'),
          value: attributes.environment.mountainView,
          formatter: (v: string) => translateEnumValue('mountainView', v),
        },
        {
          key: 'noiseLevel',
          label: getFieldLabel('noiseLevel', 'Noise Level'),
          value: attributes.environment.noiseLevel,
          formatter: (v: string) => translateEnumValue('noiseLevel', v),
        },
        {
          key: 'publicTransportQuality',
          label: getFieldLabel(
            'publicTransportQuality',
            'Public Transport Quality'
          ),
          value: attributes.environment.publicTransportQuality,
          formatter: (v: string) =>
            translateEnumValue('publicTransportQuality', v),
        },
        {
          key: 'exposureOrientation',
          label: getFieldLabel('exposureOrientation', 'Exposure Orientation'),
          value: attributes.environment.exposureOrientation,
          formatter: (v: string) =>
            translateEnumValue('exposureOrientation', v),
        },
      ].filter((f) => f.value !== null && f.value !== undefined)

      if (fields.length > 0) {
        categories.push({
          id: 'environment',
          title: getCategoryTitle('environment', 'Environment & Location'),
          iconName: 'map-pin',
          fields,
        })
      }
    }

    return categories
  }

  const categories = buildCategories()

  if (categories.length === 0) {
    return <S.EmptyState>{t.emptyState}</S.EmptyState>
  }

  return (
    <S.CardsGrid className={className}>
      {categories.map((category) => (
        <S.AttributeCard key={category.id}>
          <S.AttributeCardHeader>
            <S.CategoryTitleWrapper>
              <Icon name={category.iconName} size="small" />
              <S.CategoryTitle>{category.title}</S.CategoryTitle>
            </S.CategoryTitleWrapper>
          </S.AttributeCardHeader>
          <S.AttributeCardBody>
            {category.fields.map((field) => (
              <S.AttributeRow key={field.key}>
                <S.AttributeLabel>{field.label}</S.AttributeLabel>
                <S.AttributeValue>
                  {formatValue(field.value, field.formatter)}
                </S.AttributeValue>
              </S.AttributeRow>
            ))}
          </S.AttributeCardBody>
        </S.AttributeCard>
      ))}
    </S.CardsGrid>
  )
}
